import { useMutation, UseMutationOptions, UseMutationResult, useQueryClient } from 'react-query';

import { notificationSlice } from '../../ReduxStore/Notification/NotificationSlice';
import ApiService from '../../Services/ApiService';

import i18next from 'i18next';

import { MedicalInterviewProvider, Participant, QuestionnaireVersionFlag } from '@breathelife/types';
import { QuestionnaireEngine } from '@zinnia/form-engine-sdk';
import { useCarrierContext, useDispatch } from '../../Hooks';
import { QueryId } from '../common/common.types';

type GenerateParticipantsMutationParams = {
  shouldGenerateParticipantMedicalInterviews: boolean;
  questionnaireEngine: QuestionnaireEngine;
};

export type GenerateParticipantMutation = UseMutationResult<Participant[], unknown, GenerateParticipantsMutationParams>;

export function useGenerateParticipantsMutation(
  applicationId: string | undefined,
  flags: QuestionnaireVersionFlag[],
  shouldGenerateParticipantMedicalInterviews: boolean,
  options?: UseMutationOptions<Participant[], unknown, GenerateParticipantsMutationParams>,
): GenerateParticipantMutation {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { features } = useCarrierContext();

  return useMutation<Participant[], unknown, GenerateParticipantsMutationParams>(
    async ({ questionnaireEngine }) => {
      if (!applicationId) {
        return Promise.reject(new Error('application id is required.'));
      }

      const isMedicalInterviewFeatureEnabled =
        features.medicalInterview.enabled &&
        features.medicalInterview.provider === MedicalInterviewProvider.AuraNext &&
        flags.includes(QuestionnaireVersionFlag.AuraNextMedicalInterview);

      const withMedicalInterviews =
        shouldGenerateParticipantMedicalInterviews && !isMedicalInterviewFeatureEnabled
          ? false
          : shouldGenerateParticipantMedicalInterviews;

      const versionedAnswers = questionnaireEngine.getAnswerResolverInstance().dump();
      const response = await ApiService.generateParticipants(
        applicationId,
        versionedAnswers.v1,
        versionedAnswers.v2,
        withMedicalInterviews,
      );

      return response.data;
    },
    {
      ...options,

      onSuccess: async (data, variables, context) => {
        void options?.onSuccess?.(data, variables, context);
        void queryClient.invalidateQueries([QueryId.participants, applicationId]);
        void queryClient.removeQueries([QueryId.participantMedicalInterview]);
      },
      onError: (error, variables, context) => {
        void options?.onError?.(error, variables, context);
        dispatch(
          notificationSlice.actions.setError({
            message: i18next.t('notifications.failedToGenerateParticipants'),
          }),
        );
      },
    },
  );
}
